import React from 'react';
import { SelectPicker } from 'rsuite';
import { Formik, Form, FieldArray } from 'formik';
import Modal from '../Modal';
import { Button, TextInput } from 'shared';
import CheckTick from '../../../../assets/check_tick.svg';
import './PropertyDetailsModal.scss';

export interface PropertyDetailsData {
  numberOfRooms: number | null;
  starRating: number | null;
  chainName: string;
}

export interface PropertyForDetails {
  propertyId: string;
  propertyName: string;
}

interface PropertyDetailsModalProps {
  open: boolean;
  properties: PropertyForDetails[];
  initialData?: Record<string, PropertyDetailsData>;
  onSubmit: (data: Record<string, PropertyDetailsData>) => void;
  onClose: () => void;
}

interface PropertyFormValues {
  numberOfRooms: string;
  starRating: number | null;
  chainName: string;
}

interface FormValues {
  properties: PropertyFormValues[];
}

const starRatingOptions = [
  { label: '1 Star', value: 1 },
  { label: '2 Stars', value: 2 },
  { label: '3 Stars', value: 3 },
  { label: '4 Stars', value: 4 },
  { label: '5 Stars', value: 5 },
];

const PropertyDetailsModal: React.FC<PropertyDetailsModalProps> = ({
  open,
  properties,
  initialData = {},
  onSubmit,
  onClose,
}) => {
  const initialValues: FormValues = {
    properties: properties.map((prop) => {
      const existingData = initialData[prop.propertyId];
      return {
        numberOfRooms: existingData?.numberOfRooms?.toString() || '',
        starRating: existingData?.starRating || null,
        chainName: existingData?.chainName || '',
      };
    }),
  };

  const validate = (values: FormValues) => {
    const errors: { properties?: Array<Partial<Record<keyof PropertyFormValues, string>>> } = {};
    const propertyErrors: Array<Partial<Record<keyof PropertyFormValues, string>>> = [];

    values.properties.forEach((prop, index) => {
      const propErrors: Partial<Record<keyof PropertyFormValues, string>> = {};

      if (!prop.numberOfRooms || Number(prop.numberOfRooms) <= 0) {
        propErrors.numberOfRooms = 'Please enter a valid number of rooms';
      }

      if (!prop.starRating) {
        propErrors.starRating = 'Please select a star rating';
      }

      if (Object.keys(propErrors).length > 0) {
        propertyErrors[index] = propErrors;
      }
    });

    if (propertyErrors.length > 0) {
      errors.properties = propertyErrors;
    }

    return errors;
  };

  const handleFormSubmit = (values: FormValues) => {
    const result: Record<string, PropertyDetailsData> = {};

    properties.forEach((prop, index) => {
      result[prop.propertyId] = {
        numberOfRooms: Number(values.properties[index].numberOfRooms),
        starRating: values.properties[index].starRating,
        chainName: values.properties[index].chainName,
      };
    });

    onSubmit(result);
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeButton={true}
      size="sm"
      overflow={false}
      className="property-details-modal"
      body={
        <div className="property-details-modal__content">
          <div className="property-details-modal__icon">
            <img src={CheckTick} alt="Info" />
          </div>

          <h3 className="property-details-modal__title">Property Details</h3>

          <p className="property-details-modal__subtitle">
            Please enter details for each property
          </p>

          <Formik
            initialValues={initialValues}
            validate={validate}
            onSubmit={handleFormSubmit}
            enableReinitialize
          >
            {(formik) => (
              <Form className="property-details-modal__form">
                <div className="property-details-modal__properties-list">
                  <FieldArray name="properties">
                    {() => (
                      <>
                        {properties.map((property, index) => (
                          <div key={property.propertyId} className="property-details-modal__property-item">
                            <div className="property-details-modal__property-header">
                              <span className="property-details-modal__property-name">
                                {property.propertyName}
                              </span>
                            </div>

                            <div className="property-details-modal__property-fields">
                              <div className="property-details-modal__field">
                                <label htmlFor={`properties.${index}.numberOfRooms`}>
                                  Total Rooms *
                                </label>
                                <TextInput
                                  formik={formik}
                                  name={`properties.${index}.numberOfRooms`}
                                  dataType="number"
                                  placeholder="Enter number of rooms"
                                />
                                {formik.touched.properties?.[index]?.numberOfRooms &&
                                  (formik.errors.properties as any)?.[index]?.numberOfRooms && (
                                    <span className="property-details-modal__error">
                                      {(formik.errors.properties as any)[index].numberOfRooms}
                                    </span>
                                  )}
                              </div>

                              <div className="property-details-modal__field">
                                <label htmlFor={`properties.${index}.starRating`}>
                                  Star Rating *
                                </label>
                                <SelectPicker
                                  id={`properties.${index}.starRating`}
                                  data={starRatingOptions}
                                  placeholder="Select star rating"
                                  value={formik.values.properties[index]?.starRating}
                                  onChange={(value) =>
                                    formik.setFieldValue(`properties.${index}.starRating`, value)
                                  }
                                  searchable={false}
                                  cleanable={false}
                                  block
                                />
                                {formik.touched.properties?.[index]?.starRating &&
                                  (formik.errors.properties as any)?.[index]?.starRating && (
                                    <span className="property-details-modal__error">
                                      {(formik.errors.properties as any)[index].starRating}
                                    </span>
                                  )}
                              </div>

                              <div className="property-details-modal__field">
                                <label htmlFor={`properties.${index}.chainName`}>
                                  Chain Name (Optional)
                                </label>
                                <TextInput
                                  formik={formik}
                                  name={`properties.${index}.chainName`}
                                  placeholder="Enter chain name"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                  </FieldArray>
                </div>

                <div className="property-details-modal__actions">
                  <Button appearance="default" type="button" onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button appearance="primary" type="submit">
                    Continue
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      }
    />
  );
};

export default PropertyDetailsModal;
